import React, { useEffect, useState } from "react";
import QRCode from "qrcode";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { IndianRupee, ShieldCheck, Info, Copy, Check, Smartphone } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { Container } from "@/components/Section";

const UPI_ID = "9833388201@ybl";
const PAYEE = "PRASAD TUTOR GROUP";

// Standard UPI intent params, shared by every app link and the QR.
const upiParams = (amt) =>
  `pa=${encodeURIComponent(UPI_ID)}&pn=${encodeURIComponent(PAYEE)}&cu=INR${amt ? `&am=${encodeURIComponent(amt)}` : ""}`;

const genericUpiLink = (amt) => `upi://pay?${upiParams(amt)}`;

// App-specific deep links. Each opens the chosen app straight to its UPI-pay screen.
const APPS = [
  { key: "phonepe", label: "PhonePe", color: "bg-[#5f259f] hover:bg-[#4c1d80]", link: (a) => `phonepe://pay?${upiParams(a)}` },
  { key: "gpay", label: "Google Pay", color: "bg-[#1a73e8] hover:bg-[#1558b0]", link: (a) => `tez://upi/pay?${upiParams(a)}` },
  { key: "paytm", label: "Paytm", color: "bg-[#00baf2] hover:bg-[#0097c4]", link: (a) => `paytmmp://pay?${upiParams(a)}` },
  { key: "other", label: "Other UPI app", color: "bg-slate-800 hover:bg-slate-900", link: (a) => genericUpiLink(a) },
];

const isMobile = () => typeof navigator !== "undefined" && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || "");

export default function PayFees() {
  const [amount, setAmount] = useState("");
  const [qr, setQr] = useState("");
  const [copied, setCopied] = useState(false);
  const [fallback, setFallback] = useState(false);

  const qrValue = genericUpiLink(amount);
  useEffect(() => {
    QRCode.toDataURL(qrValue, { width: 320, margin: 2, errorCorrectionLevel: "M" }).then(setQr).catch(() => {});
  }, [qrValue]);

  const copyUpi = async () => {
    try {
      await navigator.clipboard.writeText(UPI_ID);
      setCopied(true);
      toast.success("UPI ID copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy. UPI ID: " + UPI_ID);
    }
  };

  const payWith = (app) => {
    if (!isMobile()) {
      // Desktop UPI apps aren't installed — direct the parent to scan the QR.
      setFallback(true);
      toast.info("Scan the QR with a UPI app on your phone to pay.");
      return;
    }
    const start = Date.now();
    // Open the chosen app straight to its UPI pay screen.
    window.location.href = app.link(amount);
    // If nothing took over the tab, the app isn't installed — reveal the QR + copy fallback.
    setTimeout(() => {
      if (Date.now() - start >= 1400 && document.visibilityState === "visible") {
        setFallback(true);
        toast.info(`${app.label} could not open. Scan the QR or copy the UPI ID to pay.`);
      }
    }, 1500);
  };

  return (
    <div className="py-14 sm:py-20 bg-white" data-testid="pay-fees-page">
      <Seo title="Pay Fees" description="Pay Prasad Tutor Group tuition fees securely with PhonePe, Google Pay, Paytm or any UPI app." />
      <Container>
        <p className="text-sm font-bold uppercase tracking-widest text-amber-600">Parents</p>
        <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mt-1">Pay Fees</h1>
        <p className="text-slate-600 mt-2 max-w-2xl">Pay PTG tuition fees securely using your preferred UPI app. This parents' fee-payment section is separate from the office attendance QR and from our WhatsApp contact.</p>

        <div className="mt-8 grid md:grid-cols-2 gap-8 items-start">
          {/* QR — always visible so a computer user can scan with their phone */}
          <div className="rounded-3xl border border-slate-200 bg-slate-50 p-6 text-center" data-testid="pay-fees-qr-card">
            <p className="font-bold text-blue-900 flex items-center justify-center gap-2 mb-1"><IndianRupee className="h-5 w-5" /> Parents' Fee Payment QR</p>
            <p className="text-sm text-slate-500 mb-4">Payee: <b>{PAYEE}</b><br />UPI ID: <b>{UPI_ID}</b></p>
            {qr && <img src={qr} alt="PTG fee payment UPI QR" className="mx-auto rounded-xl border border-slate-200 bg-white p-3 w-[260px]" data-testid="pay-fees-qr-image" />}
            <div className="mt-4 flex items-center justify-center gap-2 flex-wrap">
              <code className="rounded-lg bg-white border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-800" data-testid="pay-fees-upi-id">{UPI_ID}</code>
              <Button size="sm" variant="outline" onClick={copyUpi} data-testid="pay-fees-copy-upi">
                {copied ? <Check className="h-4 w-4 mr-1 text-green-600" /> : <Copy className="h-4 w-4 mr-1" />} {copied ? "Copied" : "Copy UPI ID"}
              </Button>
            </div>
            <p className="text-xs text-slate-400 mt-3">On a computer, scan this QR with PhonePe, Google Pay, Paytm or any UPI app on your phone.{amount ? ` Amount: ₹${amount}.` : " Enter the amount in your app."}</p>
          </div>

          {/* Amount + the four app buttons */}
          <div className="space-y-5">
            <div>
              <Label className="mb-1.5 block font-semibold text-slate-700">Amount (₹) — optional</Label>
              <Input type="number" inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Enter the fee amount you want to pay" data-testid="pay-fees-amount" />
              <p className="text-xs text-slate-400 mt-1">Enter the fee amount agreed with PTG. We do not set an amount for you.</p>
            </div>

            <div>
              <p className="text-sm font-semibold text-slate-700 mb-2">Choose your UPI app</p>
              <div className="grid grid-cols-2 gap-3">
                {APPS.map((app) => (
                  <Button key={app.key} size="lg" className={`h-14 text-base text-white ${app.color}`} onClick={() => payWith(app)} data-testid={`pay-fees-${app.key}-btn`}>
                    <Smartphone className="h-5 w-5 mr-2" /> {app.label}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-2">On a phone, each button opens that app with PTG's details filled in. None of these open WhatsApp. On a computer, scan the QR instead.</p>
            </div>

            {fallback && (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="pay-fees-fallback">
                <p className="font-bold">App didn't open?</p>
                <p className="mt-1">The app may not be installed. Scan the QR on the left, or copy the UPI ID <b>{UPI_ID}</b> and pay from any UPI app manually.</p>
                <Button size="sm" variant="outline" className="mt-2" onClick={copyUpi} data-testid="pay-fees-fallback-copy">
                  {copied ? <Check className="h-4 w-4 mr-1 text-green-600" /> : <Copy className="h-4 w-4 mr-1" />} Copy UPI ID
                </Button>
              </div>
            )}

            <div className="rounded-2xl border border-green-200 bg-green-50 p-4 text-sm text-green-800" data-testid="pay-fees-security">
              <p className="font-bold flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Your security</p>
              <ul className="mt-1 list-disc pl-5 space-y-1">
                <li>PTG will <b>never</b> ask for your UPI PIN, OTP or bank password. Enter your PIN only inside your own UPI app.</li>
                <li>This page does not process, store or verify payments online.</li>
              </ul>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-600" data-testid="pay-fees-confirm">
              <p className="font-bold text-slate-800 flex items-center gap-2"><Info className="h-4 w-4" /> How payment is confirmed</p>
              <p className="mt-1">Payment is <b>not</b> marked received automatically. After paying, note the UPI reference / UTR number shown in your app and share it with PTG (via the Contact page or WhatsApp). PTG verifies receipt separately from its own bank / UPI statement.</p>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
}
